import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../../components/portal/Navbar";
import Footer from "../../components/portal/Footer";
import WhatsAppBtn from "../../components/portal/WhatsAppBtn";
import PropertyCard from "../../components/portal/PropertyCard";
import { portalAPI } from "../../api";
import { TIPOS_PROPIEDAD, CIUDADES } from "../../utils";
import "./Listado.css";

const ORDEN_OPTS = [
  { value: "newest", label: "Más recientes" },
  { value: "price_asc", label: "Precio: menor a mayor" },
  { value: "price_desc", label: "Precio: mayor a menor" },
  { value: "featured", label: "Destacadas primero" },
];

// Filtros que se pueden limpiar (el orden "sort" se conserva)
const FILTROS_VACIOS = {
  operation: "", type: "", city: "", min_price: "", max_price: "", bedrooms: "", featured: "",
};

// Arma los parámetros para la API, sin vacíos
const paramsDe = (f) => {
  const params = {};
  Object.entries(f).forEach(([k, v]) => { if (v && v !== "") params[k] = v; });
  return params;
};

// ── Opciones disponibles: solo tipos y ciudades con propiedades activas, ──
// ── tomando en cuenta los demás filtros elegidos (se cruzan entre sí)     ──
const cacheFiltros = new Map(); // filtros elegidos → { data, hora }
const CINCO_MIN = 5 * 60 * 1000;
const CAMPOS_CRUZADOS = ["operation", "type", "city", "min_price", "max_price", "bedrooms", "featured"];

const useFiltrosDisponibles = (filtros) => {
  const params = {};
  CAMPOS_CRUZADOS.forEach((k) => { if (filtros[k]) params[k] = filtros[k]; });
  const clave = JSON.stringify(params);

  const vigenteEnCache = (k) => {
    const g = cacheFiltros.get(k);
    return g && Date.now() - g.hora < CINCO_MIN ? g.data : null;
  };
  const [estado, setEstado] = useState(() => ({ data: vigenteEnCache(clave), error: false }));

  useEffect(() => {
    const guardado = vigenteEnCache(clave);
    if (guardado) { setEstado({ data: guardado, error: false }); return; }
    let vigente = true;
    // Espera un instante (por si están escribiendo el precio) antes de consultar
    const t = setTimeout(() => {
      portalAPI.filtros(JSON.parse(clave))
          .then((r) => {
            cacheFiltros.set(clave, { data: r.data.data, hora: Date.now() });
            if (vigente) setEstado({ data: r.data.data, error: false });
          })
          .catch(() => { if (vigente) setEstado((e) => ({ data: e.data, error: !e.data })); });
    }, 250);
    return () => { vigente = false; clearTimeout(t); };
  }, [clave]);

  return estado;
};

/**
 * Panel de filtros.
 * - enVivo = true  (computadora): cada cambio se aplica al momento.
 * - enVivo = false (ventana en celular): los cambios van a un borrador y se aplican con el botón de abajo.
 */
const PanelFiltros = ({ filtros, onChange, onLimpiar, onEnviar, enVivo = true }) => {
  // Estado local solo para los precios (en computadora se aplican con su botón)
  const [precioMin, setPrecioMin] = useState(filtros.min_price || "");
  const [precioMax, setPrecioMax] = useState(filtros.max_price || "");

  useEffect(() => {
    setPrecioMin(filtros.min_price || "");
    setPrecioMax(filtros.max_price || "");
  }, [filtros.min_price, filtros.max_price]);

  const aplicarPrecio = () => {
    onChange({ min_price: precioMin, max_price: precioMax });
  };

  const cambiarPrecio = (campo, valor) => {
    if (campo === "min_price") setPrecioMin(valor); else setPrecioMax(valor);
    if (!enVivo) onChange({ [campo]: valor }); // en el borrador se guarda al momento
  };

  const handleLimpiar = () => {
    setPrecioMin("");
    setPrecioMax("");
    onLimpiar();
  };

  // Opciones de Tipo y Ciudad según lo que realmente hay publicado
  const { data: disponibles, error: errorFiltros } = useFiltrosDisponibles(filtros);

  let opcionesTipo;
  let opcionesCiudad;
  if (disponibles) {
    opcionesTipo = disponibles.tipos.map((t) => {
      const info = TIPOS_PROPIEDAD.find((x) => x.value === t.value);
      return { value: t.value, label: info?.label || t.value, emoji: info?.emoji || "", total: t.total };
    });
    opcionesCiudad = disponibles.ciudades.map((c) => ({ value: c.value, label: c.value, total: c.total }));
    // Si ya venía un filtro sin resultados (ej. un enlace viejo), se muestra con (0) para poder cambiarlo
    if (filtros.type && !opcionesTipo.some((o) => o.value === filtros.type)) {
      const info = TIPOS_PROPIEDAD.find((x) => x.value === filtros.type);
      opcionesTipo.push({ value: filtros.type, label: info?.label || filtros.type, emoji: info?.emoji || "", total: 0 });
    }
    if (filtros.city && !opcionesCiudad.some((o) => o.value === filtros.city)) {
      opcionesCiudad.push({ value: filtros.city, label: filtros.city, total: 0 });
    }
  } else if (errorFiltros) {
    // Si falla la consulta, se muestran todas las opciones como antes (sin números)
    opcionesTipo = TIPOS_PROPIEDAD.map((t) => ({ ...t, total: null }));
    opcionesCiudad = CIUDADES.filter((c) => c !== "Otra").map((c) => ({ value: c, label: c, total: null }));
  } else {
    opcionesTipo = [];   // cargando
    opcionesCiudad = [];
  }
  const cargandoOpciones = !disponibles && !errorFiltros;
  const conTotal = (o) => (o.total == null ? "" : ` (${o.total})`);

  return (
      <form className={`filtros-panel ${enVivo ? "card" : "filtros-panel-movil"}`}
            onSubmit={(e) => {
              e.preventDefault();
              if (enVivo) aplicarPrecio(); else onEnviar && onEnviar();
            }}>

        <div className="filtros-section">
          <label className="filtros-label">Operación</label>
          <div className="op-tabs">
            {["", "VENTA", "RENTA"].map((op) => (
                <button key={op} type="button"
                        className={`op-tab ${filtros.operation === op ? "active" : ""}`}
                    // Cambiar de operación limpia los demás filtros (el orden se conserva)
                        onClick={() => onChange({ ...FILTROS_VACIOS, operation: op })}>
                  {op === "" ? "Todos" : op === "VENTA" ? "Venta" : "Renta"}
                </button>
            ))}
          </div>
        </div>

        {(cargandoOpciones || opcionesTipo.length > 0) && (
            <div className="filtros-section">
              <label className="filtros-label">Tipo de inmueble</label>
              <select className="form-select" value={filtros.type} disabled={cargandoOpciones}
                      onChange={(e) => onChange({ type: e.target.value })}>
                <option value="">Todos</option>
                {opcionesTipo.map((t) => (
                    <option key={t.value} value={t.value}>{t.emoji} {t.label}{conTotal(t)}</option>
                ))}
              </select>
            </div>
        )}

        {(cargandoOpciones || opcionesCiudad.length > 0) && (
            <div className="filtros-section">
              <label className="filtros-label">Ciudad</label>
              <select className="form-select" value={filtros.city} disabled={cargandoOpciones}
                      onChange={(e) => onChange({ city: e.target.value })}>
                <option value="">Todas</option>
                {opcionesCiudad.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}{conTotal(c)}</option>
                ))}
              </select>
            </div>
        )}

        <div className="filtros-section">
          <label className="filtros-label">Rango de precio (MXN)</label>
          <div className="price-range">
            <input
                type="number"
                inputMode="numeric"
                placeholder="Mínimo"
                aria-label="Precio mínimo"
                className="form-input"
                value={precioMin}
                onChange={(e) => cambiarPrecio("min_price", e.target.value)}
            />
            <span>—</span>
            <input
                type="number"
                inputMode="numeric"
                placeholder="Máximo"
                aria-label="Precio máximo"
                className="form-input"
                value={precioMax}
                onChange={(e) => cambiarPrecio("max_price", e.target.value)}
            />
          </div>
          {enVivo && (
              <button type="button" className="btn btn-primary btn-sm precio-btn"
                      onClick={aplicarPrecio}>
                🔍 Aplicar precio
              </button>
          )}
        </div>

        <div className="filtros-section">
          <label className="filtros-label">Recámaras mínimas</label>
          <div className="btn-group">
            {["", "1", "2", "3", "4"].map((n) => (
                <button key={n} type="button"
                        className={`btn-option ${filtros.bedrooms === n ? "active" : ""}`}
                        onClick={() => onChange({ bedrooms: n })}>
                  {n === "" ? "Cualquiera" : `${n}+`}
                </button>
            ))}
          </div>
        </div>

        <div className="filtros-section">
          <label className="filtros-check">
            <input type="checkbox" checked={filtros.featured === "true"}
                   onChange={(e) => onChange({ featured: e.target.checked ? "true" : "" })} />
            ⭐ Solo propiedades destacadas
          </label>
        </div>

        {enVivo && (
            <div className="filtros-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={handleLimpiar}>
                Limpiar filtros
              </button>
            </div>
        )}
      </form>
  );
};

export default function Listado() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [propiedades, setPropiedades] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [cargando, setCargando] = useState(true);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);

  const filtros = {
    operation: searchParams.get("operation") || "",
    type: searchParams.get("type") || "",
    city: searchParams.get("city") || "",
    min_price: searchParams.get("min_price") || "",
    max_price: searchParams.get("max_price") || "",
    bedrooms: searchParams.get("bedrooms") || "",
    featured: searchParams.get("featured") || "",
    sort: searchParams.get("sort") || "newest",
    page: parseInt(searchParams.get("page") || "1"),
  };

  const actualizarURL = useCallback((cambios) => {
    const nuevos = { ...filtros, ...cambios, page: 1 };
    const params = new URLSearchParams();
    Object.entries(nuevos).forEach(([k, v]) => {
      if (v && v !== "" && v !== false) params.set(k, v);
      if (k === "sort" && v !== "newest") params.set(k, v);
    });
    setSearchParams(params);
  }, [filtros, setSearchParams]);

  const limpiarFiltros = () => setSearchParams(new URLSearchParams());

  // ── Ventana de filtros (celular / tablet) ──
  // Los cambios van a un borrador; solo se aplican al tocar "Ver X propiedades".
  const [borrador, setBorrador] = useState(null);
  const [conteo, setConteo] = useState(null);
  const [contando, setContando] = useState(false);
  const consultaRef = useRef(0);

  const abrirFiltros = () => {
    const { page, ...actuales } = filtros;
    setBorrador(actuales);
    setConteo(pagination.total);
    setFiltrosAbiertos(true);
  };

  const cerrarFiltros = () => {
    setFiltrosAbiertos(false); // la X descarta los cambios
    setBorrador(null);
  };

  const cambiarBorrador = (cambios) => setBorrador((b) => ({ ...b, ...cambios }));
  const limpiarBorrador = () => setBorrador((b) => ({ ...b, ...FILTROS_VACIOS }));

  const aplicarBorrador = () => {
    if (borrador) actualizarURL(borrador);
    setFiltrosAbiertos(false);
    setBorrador(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Contar resultados del borrador (con una pequeña espera para no consultar en cada tecla)
  useEffect(() => {
    if (!filtrosAbiertos || !borrador) return;
    const id = ++consultaRef.current;
    setContando(true);
    const t = setTimeout(async () => {
      try {
        const { sort, ...sinOrden } = borrador;
        const res = await portalAPI.listar({ ...paramsDe(sinOrden), limit: 1 });
        if (id === consultaRef.current) setConteo(res.data.pagination?.total ?? null);
      } catch {
        if (id === consultaRef.current) setConteo(null);
      } finally {
        if (id === consultaRef.current) setContando(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [borrador, filtrosAbiertos]);

  // Mientras la ventana está abierta: no mover la página de atrás y cerrar con Esc
  useEffect(() => {
    if (!filtrosAbiertos) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") cerrarFiltros(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previo;
      window.removeEventListener("keydown", onKey);
    };
  }, [filtrosAbiertos]);

  const hayFiltrosEnBorrador = borrador
      ? Object.keys(FILTROS_VACIOS).some((k) => borrador[k] && borrador[k] !== "")
      : false;

  const textoBotonAplicar = contando
      ? "Buscando..."
      : conteo === null
          ? "Aplicar filtros"
          : conteo === 0
              ? "Sin resultados"
              : `Ver ${conteo} propiedad${conteo !== 1 ? "es" : ""}`;

  const cambiarPagina = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", p);
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const buscar = async () => {
      setCargando(true);
      const params = {};
      Object.entries(filtros).forEach(([k, v]) => { if (v && v !== "") params[k] = v; });
      try {
        const res = await portalAPI.listar({ ...params, limit: 12 });
        setPropiedades(res.data.data);
        setPagination(res.data.pagination);
      } catch {
        setPropiedades([]);
      } finally {
        setCargando(false);
      }
    };
    buscar();
  }, [searchParams]);

  return (
      <div>
        <Navbar />
        <div className="listado-page">
          <div className="listado-header">
            <div className="container">
              <h1 className="heading-serif" style={{ fontSize: "1.75rem" }}>
                {filtros.operation === "VENTA" ? "Propiedades en Venta"
                    : filtros.operation === "RENTA" ? "Propiedades en Renta"
                        : "Propiedades"}
              </h1>
              <div className="listado-controls">
              <span className="listado-count">
                {cargando ? "Buscando..." : `${pagination.total} propiedad${pagination.total !== 1 ? "es" : ""} encontrada${pagination.total !== 1 ? "s" : ""}`}
              </span>
                <div className="listado-acciones">
                  <select className="form-select listado-orden"
                          aria-label="Ordenar propiedades"
                          value={filtros.sort}
                          onChange={(e) => actualizarURL({ sort: e.target.value })}>
                    {ORDEN_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button className="btn btn-outline-filter btn-sm btn-filtros-movil"
                          onClick={abrirFiltros}>
                    ⚙️ Filtros
                  </button>
                </div>
              </div>
            </div>
          </div>

          {filtrosAbiertos && borrador && (
              <div className="filtros-overlay" onClick={cerrarFiltros}>
                <div className="filtros-drawer" role="dialog" aria-modal="true" aria-labelledby="filtros-titulo"
                     onClick={(e) => e.stopPropagation()}>
                  <div className="filtros-drawer-header">
                    <h3 id="filtros-titulo">Filtros</h3>
                    <button type="button" onClick={cerrarFiltros} className="btn btn-ghost btn-sm"
                            aria-label="Cerrar sin aplicar">✕</button>
                  </div>

                  <div className="filtros-drawer-body">
                    <PanelFiltros filtros={borrador} onChange={cambiarBorrador}
                                  onLimpiar={limpiarBorrador} onEnviar={aplicarBorrador}
                                  enVivo={false} />
                  </div>

                  <div className="filtros-drawer-footer">
                    <button type="button" className="filtros-limpiar"
                            onClick={limpiarBorrador} disabled={!hayFiltrosEnBorrador}>
                      Limpiar
                    </button>
                    <button type="button" className="btn btn-primary filtros-aplicar"
                            onClick={aplicarBorrador}
                            disabled={!contando && conteo === 0}
                            aria-live="polite">
                      {textoBotonAplicar}
                    </button>
                  </div>
                </div>
              </div>
          )}

          <div className="container listado-body">
            <aside className="filtros-sidebar hide-mobile">
              <PanelFiltros filtros={filtros} onChange={actualizarURL} onLimpiar={limpiarFiltros} />
            </aside>

            <section className="listado-results">
              {cargando ? (
                  <div className="props-grid-listado">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="card skeleton-card">
                          <div className="skeleton" style={{ height: 200 }} />
                          <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: ".5rem" }}>
                            <div className="skeleton" style={{ height: 20, width: "60%" }} />
                            <div className="skeleton" style={{ height: 16, width: "80%" }} />
                            <div className="skeleton" style={{ height: 16, width: "50%" }} />
                          </div>
                        </div>
                    ))}
                  </div>
              ) : propiedades.length === 0 ? (
                  <div className="empty-results">
                    <div style={{ fontSize: "3rem" }}>🔍</div>
                    <h3>No encontramos propiedades</h3>
                    <p>Intenta ajustar los filtros de búsqueda</p>
                    <button className="btn btn-outline" onClick={limpiarFiltros}>Limpiar filtros</button>
                  </div>
              ) : (
                  <div className="props-grid-listado">
                    {propiedades.map((p) => <PropertyCard key={p.id} propiedad={p} />)}
                  </div>
              )}

              {!cargando && pagination.totalPages > 1 && (
                  <div className="paginacion">
                    <button className="btn btn-outline btn-sm" disabled={filtros.page <= 1}
                            onClick={() => cambiarPagina(filtros.page - 1)}>← Anterior</button>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
                        <button key={p}
                                className={`btn btn-sm ${filtros.page === p ? "btn-primary" : "btn-ghost"}`}
                                onClick={() => cambiarPagina(p)}>{p}</button>
                    ))}
                    <button className="btn btn-outline btn-sm" disabled={filtros.page >= pagination.totalPages}
                            onClick={() => cambiarPagina(filtros.page + 1)}>Siguiente →</button>
                  </div>
              )}
            </section>
          </div>
        </div>
        <Footer />
        <WhatsAppBtn />
      </div>
  );
}